import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Divider,
    CircularProgress,
    TextField,
    MenuItem,
    Button,
} from "@mui/material";
import {
    getStagedQuestions,
    releaseStagedQuestions,
    getRandomQuestionForSocialMediaDentistry,
    getRandomQuestionForSocialMediaMedicine,
} from "../../../../api/auth";
import { useSelector } from "react-redux";

const allowedEmails = [
    "rchoudhary@preclinique.co.uk",
    "yusufm2000@hotmail.co.uk",
    "htahir@preclinique.co.uk",
    "ymaududi@preclinique.co.uk",
];

const safeParse = (v) => {
    try {
        return typeof v === "string" ? JSON.parse(v) : v;
    } catch {
        return null;
    }
};

const normalizeStaged = (q) => ({
    ...q,
    choices: Array.isArray(q.choices) ? q.choices : safeParse(q.choices) ?? [],
    child_question_ids: Array.isArray(q.child_question_ids)
        ? q.child_question_ids
        : safeParse(q.child_question_ids) ?? [],
});

const StagedPreview = () => {
    const userEmail = useSelector((state) => state.auth.email);
    const userId = useSelector((state) => state.auth.userId);
    const isAllowed = allowedEmails.includes(userEmail?.toLowerCase());

    // ---------------- SOCIAL MEDIA STATE (ISOLATED) ----------------
    const [socialLoading, setSocialLoading] = useState(false);
    const [socialError, setSocialError] = useState(null);
    const [socialContent, setSocialContent] = useState(null);

    // ---------------- STAGED QUESTIONS STATE ----------------
    const [stagedQuestions, setStagedQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [universities, setUniversities] = useState([]);
    const [courses, setCourses] = useState([]);
    const [categories, setCategories] = useState([]);

    const [filters, setFilters] = useState({
        university: "",
        course: "",
        category: "",
    });

    // ---------------- SOCIAL MEDIA HANDLER ----------------
    const handleSocialFetch = async (type) => {
        setSocialLoading(true);
        setSocialError(null);
        setSocialContent(null);

        try {
            let response;

            if (type === "dentistry") {
                console.log("userid is,", userId);
                response = await getRandomQuestionForSocialMediaDentistry(userId);
            } else if (type === "medicine") {
                console.log("userid is,", userId);
                response = await getRandomQuestionForSocialMediaMedicine(userId);
            }

            if (response?.success) {
                setSocialContent({
                    type,
                    data: response.data,
                });
            } else {
                throw new Error("Invalid response");
            }
        } catch (err) {
            console.error(err);
            setSocialError("Failed to fetch question");
        } finally {
            setSocialLoading(false);
        }
    };

    // ---------------- FETCH ALL STAGED QUESTIONS ----------------
    useEffect(() => {
        const fetchAll = async () => {
            try {
                const response = await getStagedQuestions();
                const questions = response.stagedQuestions || [];

                setStagedQuestions(questions.map(normalizeStaged));

                const uniSet = new Set();
                const courseSet = new Set();
                const categorySet = new Set();

                questions.forEach((q) => {
                    if (q.university_name) uniSet.add(q.university_name);
                    if (q.course) courseSet.add(q.course);
                    if (q.category_name) categorySet.add(q.category_name);
                });

                setUniversities([...uniSet]);
                setCourses([...courseSet]);
                setCategories([...categorySet]);
            } catch (err) {
                console.error("Error fetching staged questions:", err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, []);

    // ---------------- FILTERED FETCH ----------------
    const fetchQuestions = async () => {
        try {
            const queryParams = new URLSearchParams();

            if (filters.university)
                queryParams.append("university", filters.university);
            if (filters.course) queryParams.append("course", filters.course);
            if (filters.category) queryParams.append("category", filters.category);

            const response = await getStagedQuestions(queryParams.toString());
            setStagedQuestions(
                (response.stagedQuestions || []).map(normalizeStaged)
            );
        } catch (error) {
            console.error("Error fetching staged questions:", error.message);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>

                {/* ---------------- LEFT: STAGED QUESTIONS (UNCHANGED) ---------------- */}
                <Box sx={{ flex: 2 }}>
                    <Typography variant="h4" gutterBottom>
                        Preview of Staged Questions
                    </Typography>

                    {isAllowed && (
                        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                            <TextField
                                select
                                label="University"
                                name="university"
                                value={filters.university}
                                onChange={handleChange}
                                size="small"
                            >
                                <MenuItem value="">All</MenuItem>
                                {universities.map((uni) => (
                                    <MenuItem key={uni} value={uni}>
                                        {uni}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                select
                                label="Course"
                                name="course"
                                value={filters.course}
                                onChange={handleChange}
                                size="small"
                            >
                                <MenuItem value="">All</MenuItem>
                                {courses.map((course) => (
                                    <MenuItem key={course} value={course}>
                                        {course}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                select
                                label="Category"
                                name="category"
                                value={filters.category}
                                onChange={handleChange}
                                size="small"
                            >
                                <MenuItem value="">All</MenuItem>
                                {categories.map((cat) => (
                                    <MenuItem key={cat} value={cat}>
                                        {cat}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    )}

                    {isAllowed && filters.university && (
                        <Button
                            variant="contained"
                            color="secondary"
                            sx={{ mb: 2 }}
                            onClick={async () => {
                                const confirmed = window.confirm(
                                    `Release all questions for ${filters.university}?`
                                );
                                if (!confirmed) return;

                                try {
                                    await releaseStagedQuestions(filters.university);
                                    alert(`Released for ${filters.university}`);
                                    fetchQuestions();
                                } catch (err) {
                                    console.error(err);
                                    alert("Failed to release questions.");
                                }
                            }}
                        >
                            Release All for {filters.university}
                        </Button>
                    )}

                    {loading ? (
                        <CircularProgress />
                    ) : stagedQuestions.length === 0 ? (
                        <Typography>
                            No questions currently in the staging area.
                        </Typography>
                    ) : (
                        stagedQuestions
                            .filter(
                                (q) =>
                                    (!filters.university ||
                                        q.university_name === filters.university) &&
                                    (!filters.course || q.course === filters.course) &&
                                    (!filters.category ||
                                        q.category_name === filters.category)
                            )
                            .map((question) => (
                                <Card key={question.staged_id} sx={{ mb: 2, p: 2 }}>
                                    <CardContent>
                                        <Typography variant="h6">
                                            {question.question_text}
                                        </Typography>

                                        {question.choices?.map((choice, i) => (
                                            <Typography key={i}>
                                                {i + 1}. {choice.option_text}{" "}
                                                {choice.is_correct && "✔"}
                                            </Typography>
                                        ))}
                                    </CardContent>
                                </Card>
                            ))
                    )}
                </Box>

                {/* ---------------- RIGHT: SOCIAL MEDIA GENERATOR ---------------- */}
                <Box
                    sx={{
                        flex: 1,
                        p: 2,
                        border: "1px solid #ddd",
                        borderRadius: 2,
                        height: "fit-content",
                        position: "sticky",
                        top: 20,
                    }}
                >
                    <Typography variant="h5" gutterBottom>
                        Social Media Generator
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Button
                            variant="contained"
                            onClick={() => handleSocialFetch("dentistry")}
                            disabled={socialLoading}
                        >
                            {socialLoading
                                ? "Fetching..."
                                : "Get Dentistry Question"}
                        </Button>

                        <Button
                            variant="contained"
                            onClick={() => handleSocialFetch("medicine")}
                            disabled={socialLoading}
                        >
                            {socialLoading
                                ? "Fetching..."
                                : "Get Medicine Question"}
                        </Button>
                    </Box>

                    {socialError && (
                        <Typography color="error" sx={{ mt: 2 }}>
                            {socialError}
                        </Typography>
                    )}

                    {socialContent && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="h6">
                                {socialContent.type.toUpperCase()} RESULT
                            </Typography>

                            {Object.entries(socialContent.data).map(
                                ([key, value]) => (
                                    <Box key={key} sx={{ mb: 1 }}>
                                        <Typography fontWeight="bold">
                                            {key.toUpperCase()}
                                        </Typography>
                                        <Typography sx={{ whiteSpace: "pre-wrap" }}>
                                            {typeof value === "object"
                                                ? JSON.stringify(value, null, 2)
                                                : String(value)}
                                        </Typography>
                                    </Box>
                                )
                            )}
                        </Box>
                    )}
                </Box>

            </Box>
        </Box>
    );
};

export default StagedPreview;